import type { PlanImages } from "api-types/homePlans/homePlanTypes";
import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { MISSING_ELEVATION_IMAGE_SENTINEL } from "api-types/modelTypes";
import { buildOptimizedImageUrl } from "ui/components/OptimizedImage";

interface PlanThumbnailUrlProps {
  communityPlan?: ClientLocationPlan | null;
  masterPlan?: ClientMasterPlan | null;
  elevationId?: string;
}

export const DEFAULT_ELEVATION_URL = "/static/builders/placeholder-thumbnail.svg";

export function evaluateThumbnailUrl(url: string | null | undefined) {
  return !url || url === MISSING_ELEVATION_IMAGE_SENTINEL ? DEFAULT_ELEVATION_URL : url;
}

export function generatedElevationUrl(
  planImages: PlanImages | null | undefined,
  elevationId: string | undefined,
): string | null {
  if (elevationId && planImages?.generatedElevations?.[elevationId]) {
    return evaluateThumbnailUrl(planImages.generatedElevations[elevationId]);
  } else {
    return null;
  }
}

export function getPlanThumbnailUrl({ communityPlan, masterPlan, elevationId }: PlanThumbnailUrlProps): string {
  let url: string;

  if (communityPlan) {
    const elevationUrl = generatedElevationUrl(communityPlan.planImages, elevationId);
    const u = elevationUrl ?? evaluateThumbnailUrl(communityPlan.thumbnailUrl);
    if (!u || u === DEFAULT_ELEVATION_URL) {
      return DEFAULT_ELEVATION_URL;
    }
    url = `${u}?t=${new Date(communityPlan.updatedAt).getTime()}`;
  } else if (masterPlan) {
    const elevationUrl = generatedElevationUrl(masterPlan.planImages, elevationId);
    const u = elevationUrl ?? evaluateThumbnailUrl(masterPlan.thumbnailUrl);
    if (!u || u === DEFAULT_ELEVATION_URL) {
      return DEFAULT_ELEVATION_URL;
    }

    url = `${u}?t=${new Date(masterPlan.updatedAt).getTime()}`;
  } else {
    url = "";

    // For debugging
    const elevationInfo = elevationId ? `Elevation: ${elevationId}` : "";
    const planInfoStr = elevationInfo;
    console.warn(`Missing thumbnail for ${planInfoStr}`);
  }

  return url || DEFAULT_ELEVATION_URL;
}

interface PlanThumbnailProps {
  backgroundSize: "contain" | "cover";
  imgUrl: string | null | undefined;
  backgroundImageWidth?: number;
  backgroundImageHeight?: number;
}

const PlanThumbnail = ({ imgUrl, backgroundSize, backgroundImageWidth, backgroundImageHeight }: PlanThumbnailProps) => {
  const fallback = !imgUrl || imgUrl === DEFAULT_ELEVATION_URL;

  /**
   * `style` object used because import of the PlanThumbnail.module.scss file
   *  was causing confusing compilation errors on running `yarn dev` or `yarn build`.
   */
  return (
    <div
      style={{
        backgroundSize,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        width: fallback ? "30px" : "100%",
        height: "100%",
        margin: fallback ? "0 auto" : undefined,
        backgroundImage: `url(${buildOptimizedImageUrl({
          src: imgUrl || DEFAULT_ELEVATION_URL,
          width: backgroundImageWidth,
          height: backgroundImageHeight,
        })})`,
      }}
    />
  );
};

export default PlanThumbnail;
